import {
  type TasksTileDTO,
  TasksTileKindDTO,
  PromoDTO,
} from '@/ts/types/dto/today-dto';
import {
  TODAY_TASK_LIST_MAX,
  type TodayTask,
  type TodayTasks,
  TodayTasksFace,
  TodayPromoKind,
} from '@/ts/types/component/today-page';
import {mapTodayTaskItem} from '@/ts/composables/pure/use-today-upcoming-mapper';


/**
 * The intro video, which the tile plays where it stands: `videoUrl` is the file and `posterUrl` the
 * still it rests on. A content with no image is normal and the tile falls back to its own surface.
 *
 * `contentId` is the piece rather than the file, and it travels for a second reason: reporting the
 * video watched goes through `POST /me/block-content/{contentId}/watched`, which is what closes the
 * 14-day intro window. Only transported here — that call belongs to IPD-2980.
 */
function mapVideoFace(dto: TasksTileDTO): TodayTasks | null {
  // Neither the piece nor its file can be missing: without a file there is nothing to play, and
  // this face exists to play one. Impossible by construction on the backend's side too (verified in
  // `resolveTasksTile`: the video it sends is the single piece Directus flags as home-featured,
  // which is a video by definition) — a pure safety net.
  //
  // It used to degrade to all-caught-up, whose copy claims "you completed everything", a claim a
  // malformed payload cannot back: VIDEO outranks TASK_LIST in the backend's precedence, so pending
  // tasks are plausible here. No face is honest, so no tile — a DELIBERATE divergence from the
  // upcoming mapper, which degrades everything to idle, since idle promises nothing.
  if (!dto.video?.video) {
    return null;
  }

  return {
    face: TodayTasksFace.video,
    contentId: dto.video.id,
    videoUrl: dto.video.video,
    posterUrl: dto.video.image,
    // Absent means a deployment that predates the field, not a reader with nothing on record: read
    // as "yes" it would autoplay on every visit until the backend ships — the opposite of the once
    // this exists for — and report each one to an endpoint answering 404. The field is always
    // present on this face once it does ship, so the default stops applying then (IPD-3114).
    autoplay: dto.autoplay ?? false,
  };
}

/**
 * The overflow list. Each task is flattened by the shared `mapTodayTaskItem`, which the upcoming
 * tile also uses so the two can never describe the same task differently — the synthesis of an id
 * and a route out of one of four sub-objects lives there, once.
 *
 * A task it cannot flatten comes back null (a payload whose `type` and populated sub-object do not
 * agree) and is dropped rather than rendered as an empty row. The slice is a guard, not a selection
 * rule: the backend already caps the overflow, and a payload that outgrew the design would
 * otherwise stretch the tile out of its row in the bento.
 */
function mapTaskListFace(dto: TasksTileDTO): TodayTasks | null {
  const tasks = dto.tasks
    .map((task) => mapTodayTaskItem(task))
    .filter((task): task is TodayTask => task !== null)
    .slice(0, TODAY_TASK_LIST_MAX);

  // The backend sends ALL_CAUGHT_UP for the real "nothing pending" case and an empty TASK_LIST is
  // impossible by construction, so arriving here means every task was unflattenable — a malformed
  // payload whose state the mapper cannot vouch for. Claiming "you completed everything" would be
  // a guess, so: no face, no tile, and the grid reflows.
  if (tasks.length === 0) {
    return null;
  }

  return {face: TodayTasksFace.taskList, tasks};
}

/** The backend's promo names, which read as products, to the view-model's, which read as faces. */
const PROMO_BY_DTO: Record<PromoDTO, TodayPromoKind> = {
  [PromoDTO.CHALLENGE]: TodayPromoKind.challenge,
  [PromoDTO.MEDICAL_PROGRAM]: TodayPromoKind.medicalProgram,
  [PromoDTO.NON_MEDICAL_PROGRAM]: TodayPromoKind.preventionProgram,
};

function mapPromoFace(dto: TasksTileDTO): TodayTasks | null {
  const promo = dto.promo ? PROMO_BY_DTO[dto.promo] : null;

  // Same reasoning as the pageless video: a PROMO without a promo is malformed, not "all done".
  if (!promo) {
    return null;
  }

  return {face: TodayTasksFace.promo, promo};
}

/**
 * Turns the tasks tile's slice of `GET /me/today` into the view-model the tile renders.
 *
 * The tile's own rule is that it renders whatever face it is handed and decides nothing, so this is
 * where the payload's four kinds become the four faces. Pure: nothing here reads a clock or a store.
 * Nothing in this tile is derived from the current time either — the payload's `availableSince` is
 * `@JsonIgnore`d and never arrives, because the order of the list is already the answer to "what
 * comes first" — so unlike the other Today mappers this one takes no `now`.
 */
export function mapTodayTasks(dto: TasksTileDTO): TodayTasks | null {
  switch (dto.kind) {
    case TasksTileKindDTO.VIDEO: {
      return mapVideoFace(dto);
    }
    case TasksTileKindDTO.TASK_LIST: {
      return mapTaskListFace(dto);
    }
    // The one honest source of the face: the backend said so.
    case TasksTileKindDTO.ALL_CAUGHT_UP: {
      return {face: TodayTasksFace.allCaughtUp};
    }
    case TasksTileKindDTO.PROMO: {
      return mapPromoFace(dto);
    }
    default: {
      // Exhaustiveness guard: a new kind from the backend fails this assignment at compile time.
      // At runtime an unknown kind is a payload this mapper cannot vouch for: no tile.
      const unknownKind: never = dto.kind;
      void unknownKind;
      return null;
    }
  }
}
